import classNames from 'classnames';
import { useAppSelector } from 'redux-state/configure-store.js';
import { createFavouriteApi, MAX_TOP_FAVOURITES } from 'redux-state/favourites/types.js';
import { ILocationSummary } from 'types/markerTypesV1/marker-v1-view-type.js';
import { PW_FAVOURITE_LIST } from 'utilities/consts/variable-types.js';
import { SearchbarFavouriteLocationBody } from './searchbar-fav-location-body.js';
import { SearchbarFavouriteLocation } from './searchbar-fav-location-display.js';
import { SearchbarFavouriteLocationLink } from './searchbar-fav-location-link.js';

interface ISearchBarFavLocationProps {
  favouriteTopItems: ILocationSummary[];
  currentLocationFavourite?: ILocationSummary;
  addCurrentLocation: boolean;
}

const addCurrentLocationToFavsList = ({
  favouriteTopItems,
  currentLocationFavourite,
}: {
  favouriteTopItems: ILocationSummary[];
  currentLocationFavourite?: ILocationSummary;
}) => {
  if (currentLocationFavourite) {
    const itemAlreadyExists = favouriteTopItems.find(
      (fav) => createFavouriteApi(fav) === createFavouriteApi(currentLocationFavourite),
    );
    if (!itemAlreadyExists) {
      return favouriteTopItems.concat(currentLocationFavourite);
    }
  }
  return favouriteTopItems;
};

const getTopFavouriteItemsWithCurrentLocation = ({
  favouriteTopItems,
  currentLocationFavourite,
  addCurrentLocation,
}: ISearchBarFavLocationProps) => {
  const items = addCurrentLocation
    ? addCurrentLocationToFavsList({ favouriteTopItems, currentLocationFavourite })
    : favouriteTopItems;
  return items.filter(Boolean).slice(0, MAX_TOP_FAVOURITES);
};

export const SearchBarFavouriteDisplayLocations = ({
  currentLocationFavourite,
  addCurrentLocation,
}: Pick<ISearchBarFavLocationProps, 'addCurrentLocation' | 'currentLocationFavourite'>) => {
  const favouriteTopItems = useAppSelector((s) => s.FAVOURITES.topItems);

  const favouritesDisplayList = getTopFavouriteItemsWithCurrentLocation({
    favouriteTopItems,
    currentLocationFavourite,
    addCurrentLocation,
  }).map((favListItem) => {
    const { link, value } = favListItem;

    if (link && currentLocationFavourite) {
      const isSavedFav = favouriteTopItems.find(
        (topItem) => createFavouriteApi(favListItem) === createFavouriteApi(topItem),
      );

      const listItemClasses = classNames('SearchBar-favourites-list-item', {
        'u-flexJustifyCenter': !value,
        'SearchBar-favourites-list-item--disabled': !isSavedFav,
      });

      return (
        <li key={favListItem.key} data-comp="fav-location" className={listItemClasses}>
          <SearchbarFavouriteLocationLink disabled={!isSavedFav} link={link.url}>
            <SearchbarFavouriteLocation favourite={favListItem}>
              <SearchbarFavouriteLocationBody favourite={favListItem} />
            </SearchbarFavouriteLocation>
          </SearchbarFavouriteLocationLink>
        </li>
      );
    }
    return null;
  });
  if (favouritesDisplayList.length) {
    return (
      <ul className="SearchBar-favourites-list" data-testid={PW_FAVOURITE_LIST}>
        {favouritesDisplayList}
      </ul>
    );
  }
  return null;
};
